import { useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, type Operator } from "../../api";
import { Avatar } from "../../components/Avatar";
import { precheckImage } from "../../components/Media";
import { useI18n, type Lang } from "../../lib/i18n";
import { Section, SettingRow, StatusLine, useStatus } from "./common";

/**
 * The operator's own name, picture and language. The name and picture are what
 * every customer sees next to a reply, so they should be a person's — never an
 * e-mail address.
 */
export function ProfileTab({
  operator,
  onChange,
  onLang,
}: {
  operator: Operator;
  onChange: (next: Operator) => void;
  onLang: (lang: Lang) => Promise<void>;
}): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.profile;
  const [name, setName] = useState(operator.displayName);
  const [status, show, fail] = useStatus();
  const file = useRef<HTMLInputElement>(null);
  const trimmed = name.trim();

  async function saveName(event: Event): Promise<void> {
    event.preventDefault();
    try {
      const res = await api.updateProfile({ displayName: trimmed });
      onChange({
        ...operator,
        displayName: res.operator.displayName,
        color: res.operator.color,
      });
      show("ok", t.nameSaved);
    } catch (err) {
      fail(err);
    }
  }

  async function upload(picked: File): Promise<void> {
    const problem = precheckImage(picked, 2 * 1024 * 1024);
    if (problem) {
      show("error", problem.kind === "type" ? all.composer.imageType : t.photoTooBig);
      return;
    }
    try {
      const res = await api.uploadAvatar(picked);
      onChange({ ...operator, avatarUrl: res.avatarUrl });
      show("ok", t.photoSaved);
    } catch (err) {
      fail(err);
    }
  }

  return (
    <>
      <Section title={t.seenTitle} intro={t.seenIntro}>
        <SettingRow title={t.photo} hint={t.photoHint}>
          <div class="profile-row">
            <Avatar name={operator.displayName} color={operator.color} url={operator.avatarUrl} size={56} />
            <div class="profile-actions">
              <button class="btn sm" onClick={() => file.current?.click()}>
                {t.uploadPhoto}
              </button>
              {operator.avatarUrl ? (
                <button
                  class="btn sm"
                  onClick={() =>
                    void api
                      .deleteAvatar()
                      .then(() => onChange({ ...operator, avatarUrl: null }))
                      .catch(fail)
                  }
                >
                  {t.removePhoto}
                </button>
              ) : null}
              <input
                ref={file}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                hidden
                onChange={(e) => {
                  const picked = e.currentTarget.files?.[0];
                  e.currentTarget.value = "";
                  if (picked) void upload(picked);
                }}
              />
            </div>
          </div>
        </SettingRow>
        <SettingRow title={t.displayName} hint={t.displayNameHint} htmlFor="profile-name">
          <form onSubmit={(e) => void saveName(e)} class="inline-actions">
            <input
              id="profile-name"
              type="text"
              value={name}
              maxLength={40}
              onInput={(e) => setName(e.currentTarget.value)}
            />
            <button class="btn primary" disabled={trimmed.length < 2 || trimmed === operator.displayName}>
              {t.saveName}
            </button>
          </form>
        </SettingRow>
        <StatusLine status={status} />
      </Section>

      <Section title={t.preferences}>
        <SettingRow title={t.language} hint={t.languageHint}>
          <div class="segmented" role="radiogroup" aria-label={t.language}>
            {(
              [
                ["tr", "Türkçe"],
                ["en", "English"],
              ] as Array<[Lang, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                role="radio"
                aria-checked={lang === value}
                onClick={() => void onLang(value).catch(fail)}
              >
                {label}
              </button>
            ))}
          </div>
        </SettingRow>
      </Section>

      <Section title={t.account}>
        <SettingRow title={t.email} hint={t.emailHint}>
          <span class="value-text">{operator.email}</span>
        </SettingRow>
        <SettingRow title={t.role}>
          <span class="chip">{all.role[operator.role]}</span>
        </SettingRow>
      </Section>
    </>
  );
}
